import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isMobile } from 'react-device-detect';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import Table from 'common/components/table/table-material';
import { getGuests, fetchGuestsForExport } from '../guestControl/guestControlActions';

const EMPTY_FILTERS = {
  guest_type: '',
  invited_name: '',
  invited_cpf: '',
  invited_email: '',
  inviter_name: '',
  status: '',
  invited_from: '',
  invited_to: '',
};

const FILTER_DEBOUNCE_MS = 400;

const STATUS_LABEL = {
  pending: 'Pendente',
  accepted: 'Aceito',
  revoked: 'Revogado',
  expired: 'Expirado',
};

const TYPE_LABEL = {
  friend: 'Amigo',
  dependent: 'Dependente',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch (e) {
    return '—';
  }
};

export function GuestsList(props) {
  const { list, listLoading } = props.guestControl;
  const stationId = props.auth?.user?.establishments?.[0]?.stations?.[0]?.id;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [exporting, setExporting] = useState(false);

  const onForbidden = useCallback(() => {
    if (props.router?.push) props.router.push('/owner');
  }, [props.router]);

  useEffect(() => {
    if (!stationId) return undefined;
    const handle = setTimeout(() => {
      props.getGuests(stationId, filters, { onForbidden });
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    stationId,
    filters.guest_type,
    filters.invited_name,
    filters.invited_cpf,
    filters.invited_email,
    filters.inviter_name,
    filters.status,
    filters.invited_from,
    filters.invited_to,
  ]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);
  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v !== '' && v != null),
    [filters]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const data = await props.fetchGuestsForExport(stationId, filters);
      const items = data?.items || [];
      const rows = items.map((g) => ({
        Tipo: TYPE_LABEL[g.guest_type] || g.guest_type,
        Convidado: g.invited_name || '',
        CPF: g.invited_cpf || '',
        'E-mail': g.invited_email || '',
        Telefone: g.invited_phone || '',
        'Convidado por': g.inviter ? g.inviter.name : '',
        'CPF do convidante': g.inviter ? g.inviter.cpf : '',
        Status: STATUS_LABEL[g.status] || g.status,
        Convite: g.invited_at ? new Date(g.invited_at).toLocaleString('pt-BR') : '',
        Aceite: g.accepted_at ? new Date(g.accepted_at).toLocaleString('pt-BR') : '',
        Revogado: g.revoked_at ? new Date(g.revoked_at).toLocaleString('pt-BR') : '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Convidados');
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `convidados-${stamp}.xlsx`);
    } catch (e) {
      console.error('Falha ao exportar planilha:', e);
    } finally {
      setExporting(false);
    }
  }, [filters, props]);

  const columns = [
    { render: (r) => TYPE_LABEL[r.guest_type] || r.guest_type, title: 'Tipo' },
    { render: (r) => r.invited_name, title: 'Convidado' },
    { render: (r) => r.invited_cpf, title: 'CPF' },
    { render: (r) => r.invited_email, title: 'E-mail' },
    {
      render: (r) => (r.inviter ? `${r.inviter.name} (${r.inviter.cpf})` : '—'),
      title: 'Convidado por',
    },
    { render: (r) => STATUS_LABEL[r.status] || r.status, title: 'Status' },
    { render: (r) => formatDate(r.invited_at), title: 'Convite' },
    { render: (r) => formatDate(r.accepted_at), title: 'Aceite' },
    { render: (r) => formatDate(r.revoked_at), title: 'Revogado' },
  ];

  return (
    <section id="guests-list" style={{ width: isMobile ? '90vw' : undefined }}>
      <GeneralGrid container spacing={3}>
        <GeneralGrid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Convidados
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Para ajustar permissões e limites, use{' '}
            <a href="#/owner/configuracoes/controle-de-convidados">
              Configurações → Controle de convidados
            </a>
            .
          </Typography>
        </GeneralGrid>

        <GeneralGrid item xs={12}>
          {(list.items || []).length > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: 16,
              }}>
              <button
                onClick={handleExport}
                aria-label="Baixar planilha"
                disabled={exporting}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: exporting ? 'not-allowed' : 'pointer',
                  padding: 0,
                  opacity: exporting ? 0.5 : 1,
                }}>
                <FontAwesomeIcon
                  icon={faBook}
                  style={{
                    color: 'green',
                    fontSize: '25px',
                    verticalAlign: 'middle',
                  }}
                />
              </button>
            </div>
          )}

          <GeneralGrid container spacing={2} style={{ marginBottom: 16 }}>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Tipo"
                value={filters.guest_type}
                onChange={(e) => setFilter('guest_type', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="friend">Amigo</MenuItem>
                <MenuItem value="dependent">Dependente</MenuItem>
              </TextField>
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilter('status', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="accepted">Aceito</MenuItem>
                <MenuItem value="revoked">Revogado</MenuItem>
                <MenuItem value="expired">Expirado</MenuItem>
              </TextField>
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                label="Nome do convidado"
                value={filters.invited_name}
                onChange={(e) => setFilter('invited_name', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
              />
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                label="CPF"
                value={filters.invited_cpf}
                onChange={(e) => setFilter('invited_cpf', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
              />
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                label="E-mail"
                value={filters.invited_email}
                onChange={(e) => setFilter('invited_email', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
              />
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                label="Convidado por"
                value={filters.inviter_name}
                onChange={(e) => setFilter('inviter_name', e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
              />
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                label="Convite de"
                type="date"
                value={filters.invited_from}
                onChange={(e) => setFilter('invited_from', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="outlined"
                fullWidth
              />
            </GeneralGrid>
            <GeneralGrid item xs={12} sm={6} md={3}>
              <TextField
                label="Convite até"
                type="date"
                value={filters.invited_to}
                onChange={(e) => setFilter('invited_to', e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                variant="outlined"
                fullWidth
              />
            </GeneralGrid>
            {hasActiveFilters && (
              <GeneralGrid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button size="small" onClick={clearFilters} variant="outlined">
                    Limpar filtro
                  </Button>
                </Box>
              </GeneralGrid>
            )}
          </GeneralGrid>
        </GeneralGrid>

        <GeneralGrid item xs={12}>
          {listLoading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <LoadingProgress />
            </Box>
          ) : (
            <Table
              hasToolbar={false}
              defaultSelected={{}}
              title={`Convidados (${list.total})`}
              columns={columns}
              rows={list.items}
              compressedTable
              mobileTable={isMobile}
              hasBorder={!isMobile}
            />
          )}
        </GeneralGrid>
      </GeneralGrid>
    </section>
  );
}

GuestsList.propTypes = {
  guestControl: PropTypes.shape({
    list: PropTypes.shape({
      items: PropTypes.array,
      total: PropTypes.number,
    }),
    listLoading: PropTypes.bool,
  }),
  auth: PropTypes.object,
  router: PropTypes.shape({ push: PropTypes.func }),
  getGuests: PropTypes.func,
  fetchGuestsForExport: PropTypes.func,
};

const mapStateToProps = (state) => ({
  guestControl: state.guestControl,
  auth: state.auth,
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getGuests, fetchGuestsForExport }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(GuestsList);
